import React, { useState, useEffect } from "react";
import { Dialog, Button } from "@mui/material";
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useTheme } from "../../../../components/hooks/useTheme";
import colorConfigs from "../../../../configs/colorConfigs";
import { post_comment } from "../../../../services/comment";
import useAuth from "../../../../components/hooks/useAuth";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";
import moment from "moment";
import useGeneral from "../../../../components/hooks/useGeneral";

type Props = {
    customerId: string;
    addNotePopup: boolean;
    setAddNotePopup: () => void;
}

export default function AddNote({ customerId, addNotePopup, setAddNotePopup }: Props) {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const { theme, themeStyles } = useTheme();
    const [notes, setNotes] = useState<string>("");
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    const resetForm = () => {
        setNotes("");
        setEditorState(EditorState.createEmpty());
    };

    useEffect(() => {
        if (addNotePopup) {
            resetForm();
        }
    }, [addNotePopup]);

    const handleNotes = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const currentDate = new Date();

        await post_comment({
            attributeName: "Customer",
            notes,
            createdDate: moment(currentDate).format(dateFormat + " hh:mm:ss a"),
            attributeId: parseInt(customerId),
        }, auth?.id, tenantId)
            .then(() => {
                setAddNotePopup();
                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
            });
    };

    const [editorState, setEditorState] = useState(() =>
        EditorState.createEmpty()
    );

    useEffect(() => {
        setNotes(draftToHtml(convertToRaw(editorState.getCurrentContent())));
    }, [editorState]);

    return (
        <Dialog
            open={addNotePopup}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`px-[24px] py-[10px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320.591 320.591"
                    className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                    onClick={() => setAddNotePopup()}
                >
                    <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                    <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                </svg>
                <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                    <div className="w-full mt-2">
                        <div className={`w-full`}>
                            <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                <div className="flex items-center gap-1.5">
                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Note</h2>
                                </div>
                                <IconButton onClick={() => setAddNotePopup()}>
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            <div className="mt-2 p-2">

                                <div className="w-full">
                                    <form onSubmit={(e) => handleNotes(e)}>
                                        <div>
                                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Notes</label>
                                            <Editor
                                                editorStyle={{
                                                    border: `1px solid #D0D0D0`,
                                                    backgroundColor: "white",
                                                    minHeight: "150px",
                                                    maxHeight: "150px",
                                                    overflow: "auto",
                                                    padding: "10px",
                                                    cursor: "text"
                                                }}
                                                editorState={editorState}
                                                wrapperClassName="wrapper-class"
                                                editorClassName="editor-class"
                                                toolbarClassName="toolbar-class"
                                                onEditorStateChange={setEditorState}
                                                toolbar={{
                                                    inline: { inDropdown: true },
                                                    list: { inDropdown: true },
                                                    textAlign: { inDropdown: true },
                                                    link: { inDropdown: true },
                                                    history: { inDropdown: true },
                                                }}
                                            />
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-4 mt-6">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.main.bg
                                                }}
                                                type="submit"
                                            >
                                                Save
                                            </Button>

                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={() => setAddNotePopup()}>
                                                Cancel
                                            </Button>

                                        </div>
                                    </form>


                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
